<template>
  <div id="app">
    <MaintenancePage
      v-if="isMaintenance"
      :message="maintenanceMessage"
    />

    <UpdateBannerV2
      :has-new-version="hasNewVersion"
      :is-embed-route="isEmbedRoute"
      :is-public-route="isPublicRoute"
      @reload-app="reloadApp"
      @dismiss-update="dismissUpdate"
    />

    <AppLoadingScreen
      :is-loading="isLoading"
      :is-embed-route="isEmbedRoute"
    />
    
    <template v-if="!isLoading || isEmbedRoute || isPublicRoute">
      <!-- MAINTENANCE : l'application dessous devient inerte. `MaintenancePage`
           est un calque plein écran, pas un frère qui remplace : l'application
           restait lisible, focalisable au clavier, et le lecteur d'écran
           annonçait DEUX titres de niveau 1 — « Mise à jour en cours » et celui
           de la page masquée. `inert` coupe le focus et les clics, `aria-hidden`
           la retire de l'arbre d'accessibilité ; `|| null` retire l'attribut
           plutôt que d'écrire `inert="false"`, qui rendrait inerte à jamais.
           On ne démonte pas : la page reprend telle quelle quand le serveur
           revient, sans perdre ce qui était à l'écran. Ce qui est téléporté
           dans <body> échappe à cet attribut : `MaintenancePage` le rend
           inerte lui-même. -->
      <AppLayout
        :inert="isMaintenance || null"
        :aria-hidden="isMaintenance || null"
        :show-header="showHeader"
        :is-embed-route="isEmbedRoute"
        :is-public-route="isPublicRoute"
        :mobile-sidebar-open="mobileSidebarOpen"
        :sidebar-collapsed="sidebarCollapsed"
        :is-admin="isAdmin"
        :is-super-admin="isSuperAdmin"
        :has-acces-pointeuse="hasAccesPointeuse"
        :user="user"
        :call-in-progress="callInProgress"
        :call-duration="callDuration"
        :current-call-quote="currentCallQuote"
        :current-call-quote-id="currentCallQuoteId"
        :saving-call="savingCall"
        :call-direction="callDirection"
        :format-timer="formatTimer"
        @toggle-mobile-sidebar="toggleMobileSidebar"
        @close-mobile-sidebar="closeMobileSidebar"
        @toggle-sidebar="toggleSidebar"
        @logout="handleLogout"
        @abandon-call="abandonCall"
        @mark-no-answer="markCallAsNoAnswer"
        @mark-not-interested="markCallAsNotInterested"
        @schedule-relance="markCallScheduleRelance"
        @validate-info="openValidateInfoModal"
      />

      <!-- Modales d'issue d'appel : pilotées par la bannière globale, donc montées ici
           pour rester opérantes sur toutes les pages (pas seulement le détail du devis).
           JAMAIS sur une page ouverte aux visiteurs, en revanche : elles n'y ont aucun
           sens, et un appel authentifié parti de là répondrait 401 — pris pour une
           session expirée, il expédierait les formateurs vers l'écran de connexion
           du back-office.
           ET JAMAIS AVANT QUE LE ROUTEUR N'AIT RÉSOLU LA NAVIGATION INITIALE :
           `isPublicRoute` se calcule sur `route.path`, qui vaut encore « / » à cet
           instant. Toute page ouverte en chargement complet — portail, rubrique
           déplacée, adresse inconnue — monterait donc ces modales le temps d'un
           battement. -->
      <template v-if="isRouterReady && !isEmbedRoute && !isPublicRoute">
      <QuoteNoAnswerModalV2
        :model-value="showNoAnswerModal"
        :saving="savingCall"
        @update:model-value="showNoAnswerModal = false"
        @confirm="submitNoAnswer"
      />
      <QuoteNotInterestedModal
        :model-value="showNotInterestedModal"
        :saving="savingNotInterested"
        @update:model-value="closeNotInterestedModal"
        @confirm="confirmNotInterested"
      />
      <QuoteScheduleRelanceModal
        :model-value="showRelanceModal"
        :saving="savingRelance"
        :quote="currentCallQuote"
        @update:model-value="closeRelanceModal"
        @confirm="confirmRelance"
      />

      <!-- Popup « Analyse du besoin » : questionnaire ouvert par-dessus la page
           courante (bouton « Analyse du besoin » du bandeau d'appel, ou « Éditer »
           depuis le détail du devis). -->
      <!-- Montée seulement pendant l'ouverture ; `:key` remonte l'hôte (donc
           recharge) quand la fenêtre sert une autre demande dans la même session,
           sans quoi la deuxième ouverture montrerait la validation de la première. -->
      <QuoteBesoinDialogHost
        v-if="besoinModalOpen && besoinQuoteId"
        :key="besoinQuoteId"
        :quote-id="besoinQuoteId"
        @close="closeBesoinModal"
      />
      </template>
    </template>
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount } from 'vue'
import { useAuth } from './composables/auth/useAuth.js'
import { useTableColumnHover } from './composables/ui/useTableColumnHover.js'
import { useVersionCheck } from './composables/useVersionCheck.js'
import { useAppRouting } from './composables/useAppRouting.js'
import { useAppSidebar } from './composables/useAppSidebar.js'
import { useAppInitialization } from './composables/useAppInitialization.js'
import { useAppCallHandlers } from './composables/calls/useAppCallHandlers.js'
import { useActivityTracking } from './composables/useActivityTracking.js'
import AppLayout from './components/layout/AppLayout.vue'
import UpdateBannerV2 from './components/layout/UpdateBannerV2.vue'
import AppLoadingScreen from './components/layout/AppLoadingScreen.vue'
import MaintenancePage from './components/layout/MaintenancePage.vue'
import QuoteNoAnswerModalV2 from './components/quote-detail/QuoteNoAnswerModalV2.vue'
import QuoteNotInterestedModal from './components/quote-detail/QuoteNotInterestedModal.vue'
import QuoteScheduleRelanceModal from './components/quote-detail/QuoteScheduleRelanceModal.vue'
import QuoteBesoinDialogHost from './components/quote-besoin/QuoteBesoinDialogHost.vue'
import { useMaintenance } from './composables/useMaintenance.js'
import { usePermissions } from './composables/auth/usePermissions.js'
import { useSessionExpiryRedirect } from './composables/auth/useSessionExpiryRedirect.js'

const { isMaintenance, maintenanceMessage } = useMaintenance()

const { logout, user } = useAuth()
// Source unique des droits (admin ET super_admin) et de l'accès super_admin
// uniquement — jamais de comparaison littérale sur user.role ici.
const { isAdmin, isSuperAdmin, hasAccesPointeuse } = usePermissions()
const { hasNewVersion, reloadApp, dismissUpdate } = useVersionCheck()
const { isEmbedRoute, isPublicRoute, isRouterReady, showHeader } = useAppRouting()
const {
  mobileSidebarOpen,
  sidebarCollapsed,
  toggleMobileSidebar,
  closeMobileSidebar,
  toggleSidebar
} = useAppSidebar()
const { isLoading, initializeApp, cleanup: cleanupInit } = useAppInitialization({ isEmbedRoute, isPublicRoute })
// Tracking front du panneau d'activité commerciale (D7/D25) : émetteur
// silencieux, actif uniquement pour un utilisateur BO connecté (voir garde
// interne du composable) — jamais sur les routes publiques/portails.
const { init: initActivityTracking, cleanup: cleanupActivityTracking } = useActivityTracking()
// Session expirée : un 401 sur n'importe quel appel prévient et renvoie vers la
// connexion, au lieu de laisser chaque écran afficher le message
// d'authentification du serveur au bout de son propre formulaire.
const { install: installSessionExpiryRedirect } = useSessionExpiryRedirect()
const {
  callInProgress,
  quoteId: currentCallQuoteId,
  quote: currentCallQuote,
  callDuration,
  callDirection,
  savingCall,
  savingNotInterested,
  savingRelance,
  formatTimer,
  abandonCall,
  markCallAsNoAnswer,
  markCallAsNotInterested,
  markCallScheduleRelance,
  openValidateInfoModal,
  showNoAnswerModal,
  showNotInterestedModal,
  showRelanceModal,
  besoinModalOpen,
  besoinQuoteId,
  closeBesoinModal,
  submitNoAnswer,
  confirmNotInterested,
  confirmRelance,
  closeNotInterestedModal,
  closeRelanceModal,
  cleanup: cleanupCall
} = useAppCallHandlers()

const handleLogout = async () => {
  await logout()
}

useTableColumnHover()

onMounted(() => {
  installSessionExpiryRedirect()
  initializeApp()
  initActivityTracking()
})

onBeforeUnmount(() => {
  cleanupInit()
  cleanupCall()
  cleanupActivityTracking()
})
</script>

<style scoped>
#app {
  min-height: 100vh;
}
</style>

